import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import type { ClientLifecycleEvent } from '@siliconx/shared';
import { lifecycleLabel } from '@siliconx/shared/ui/LifecycleBadge';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import type { ClientFmvSnapshot } from '../asset-detail/asset-detail-types';
import { useOrgFeature } from '../../lib/orgFeatures';

// ---------------------------------------------------------------------------
// SIL-565: primitives shared across the asset-detail tab modules
// (OverviewTab / ValueTab / HealthTab / SustainabilityTab / LifecycleTab)
// and AssetDetailV2's own top-of-page tiles. Split out of the single
// AssetDetailV2.tsx file so each tab can import only what it needs.
// ---------------------------------------------------------------------------

export interface ApiError extends Error {
  status?: number;
}

/** Minimal client-side CSV download — every module's "Export CSV". */
export function downloadCsv(filename: string, header: string[], rows: (string | number | null)[][]) {
  const esc = (v: string | number | null) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-zinc-200 bg-white p-6 ${className}`}>{children}</div>
  );
}

export function CardLabel({ children }: { children: React.ReactNode }) {
  return (
    <SectionLabel>
      {children}
    </SectionLabel>
  );
}

/**
 * Consolidated empty-state pattern for the Health tab's four cards (SIL-336)
 * — each one previously rendered a bare terminal sentence ("No score history
 * yet.", "Not yet scored.", "No telemetry collected in the last 30 days.")
 * with no cause and no next step, in four different copy voices. All 16 seed
 * assets in the demo org are telemetry-dark or no-data, so this is the
 * default read of the page today, not a rare edge case — every empty branch
 * states cause + remedy, in one voice, instead of a dead end.
 */
export function EmptyState({
  icon: Icon,
  message,
  remedy,
}: {
  icon: LucideIcon;
  message: string;
  remedy?: React.ReactNode;
}) {
  return (
    <div className="mt-3 flex items-start gap-2.5 text-sm text-zinc-400">
      <Icon className="mt-0.5 size-4 flex-none text-zinc-300" aria-hidden />
      <p>
        {message}
        {remedy && <> {remedy}</>}
      </p>
    </div>
  );
}

/** Cause sentence shared by the three telemetry-dependent Health-tab cards
 * (score trend, subsystem scores, device telemetry) — all three are empty
 * for exactly the same reason, so they read as one story rather than three
 * unrelated dead ends. */
export const NO_TELEMETRY_AGENT_MESSAGE = 'No telemetry agent is reporting for this asset.';

/**
 * Shared remedy link for the telemetry-dependent empty states — routes to
 * the integrations page where a customer connects telemetry providers.
 *
 * SIL-1036: `orgFeature: 'fleetData'` — /integrations is business-only (an
 * individual customer_org has no fleet integrations to connect), so this
 * renders nothing for one rather than a link into a page it can't reach.
 * `NO_TELEMETRY_AGENT_MESSAGE` alone still reads as a complete sentence with
 * the remedy gone, so no caller needs its own copy branch.
 */
export function ConnectIntegrationRemedy() {
  const canFleetData = useOrgFeature('fleetData');
  if (!canFleetData) return null;
  return (
    <>
      Connect an integration to start scoring health —{' '}
      <Link to="/integrations" className="font-medium text-brand-600 hover:underline">
        go to Integrations
      </Link>
      .
    </>
  );
}

/**
 * `muted` (SIL-1291): the dot is a state indicator, not decoration — every
 * tile rendered it green regardless of whether the value below it was "—".
 * Callers decide emptiness (each tile's own null-check), TileLabel only
 * renders the boolean: neutral/grey when muted, brand green otherwise.
 */
export function TileLabel({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500">
      <span className={`size-[7px] rounded-full ${muted ? 'bg-zinc-300' : 'bg-brand-500'}`} />
      {children}
    </div>
  );
}

/** "View table / Hide table · Export CSV" footer strip shared by modules. */
export function TableToggleFooter({
  open,
  onToggle,
  onExport,
}: {
  open: boolean;
  onToggle: () => void;
  onExport: () => void;
}) {
  return (
    <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-2.5">
      <button
        type="button"
        onClick={onToggle}
        className="text-xs font-semibold text-brand-700 hover:text-brand-800"
      >
        {open ? 'Hide table' : 'View table'}
      </button>
      <button
        type="button"
        onClick={onExport}
        className="text-xs font-semibold text-zinc-500 hover:text-zinc-700"
      >
        Export CSV
      </button>
    </div>
  );
}

/**
 * Inline mini data table used by the per-module chart/table toggles.
 *
 * `numericCols` (SIL-338): column indices holding money/score/count values —
 * DESIGN.md's Tabular Money Rule right-aligns those specifically, leaving the
 * label column (always index 0 in every caller today) left-aligned.
 */
export function MiniTable({
  header,
  rows,
  footer,
  numericCols = [],
}: {
  header: string[];
  rows: (string | number)[][];
  footer?: string;
  numericCols?: number[];
}) {
  const cols = `repeat(${header.length}, minmax(0, 1fr))`;
  const alignFor = (j: number) => (numericCols.includes(j) ? 'text-right' : 'text-left');
  return (
    <div className="mt-2.5 overflow-hidden rounded-md border border-zinc-200">
      <div
        className="grid bg-zinc-50 px-3 py-1.5 text-xs uppercase tracking-widest text-zinc-500"
        style={{ gridTemplateColumns: cols }}
      >
        {header.map((h, j) => (
          <span key={h} className={alignFor(j)}>
            {h}
          </span>
        ))}
      </div>
      {rows.map((r, i) => (
        <div
          key={i}
          className="grid border-t border-zinc-100 px-3 py-1.5 font-mono text-[13px] text-zinc-800"
          style={{ gridTemplateColumns: cols }}
        >
          {r.map((c, j) => (
            <span key={j} className={alignFor(j)}>
              {c}
            </span>
          ))}
        </div>
      ))}
      {footer && (
        <div className="border-t border-zinc-100 px-3 py-1.5 text-xs text-zinc-500">
          {footer}
        </div>
      )}
    </div>
  );
}

/** Tiny SVG sparkline (mock-style) — recharts is overkill at tile size. */
export function MiniSpark({
  values,
  stroke,
  fill,
}: {
  values: number[];
  stroke: string;
  fill?: boolean;
}) {
  if (values.length < 2) {
    return <div className="mt-2.5 h-[46px] text-xs text-zinc-400">Not enough data</div>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i): [number, number] => [
    (i / (values.length - 1)) * 120,
    32 - ((v - min) / span) * 28,
  ]);
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <svg viewBox="0 0 120 36" preserveAspectRatio="none" className="mt-2.5 block h-[46px] w-full">
      {fill && (
        <path
          d={`M${pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' L')} L120,36 L0,36 Z`}
          fill={stroke}
          opacity={0.12}
        />
      )}
      <polyline
        points={line}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export const money = (v: number) =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Oldest record within the trailing 30 days, or null → no delta shown. */
export function baseline30d<T>(rows: T[], dateOf: (r: T) => string | null): T | null {
  const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
  const inWindow = rows
    .filter((r) => {
      const d = dateOf(r);
      return d != null && new Date(d).getTime() >= cutoff;
    })
    .sort((a, b) => new Date(dateOf(a)!).getTime() - new Date(dateOf(b)!).getTime());
  return inWindow.length >= 2 ? inWindow[0]! : null;
}

/** Collapses back-to-back rows for the same calendar day and the same FMV
 * value down to the newest one. The market-comp trigger can re-fire several
 * times for one asset in a short window without the value actually moving
 * (SIL-315 doc on `fmv-snapshot-writer.ts`'s `skipIfUnchanged`) — left
 * un-collapsed, those bursts fill history views with identical rows and hide
 * real history further down. `history` arrives newest-first, so keeping the
 * first row of each same-day/same-value run keeps the newest. Distinct
 * values on the same day (a real intraday change) are never merged. Shared
 * by the always-visible FmvTile and the Value tab's FMV history card
 * (SIL-925) so both show the same deduped history.
 */
export function collapseSameDayDuplicates(history: ClientFmvSnapshot[]): ClientFmvSnapshot[] {
  return history.filter((row, i) => {
    const prev = history[i - 1];
    if (!prev) return true;
    const sameDay = prev.pricedAt.slice(0, 10) === row.pricedAt.slice(0, 10);
    return !(sameDay && prev.fmvMedUsd === row.fmvMedUsd);
  });
}

/** Colored delta chip — used by the Value and Health tabs' telemetry/price
 * delta call-outs. */
export function DeltaChip({ text, warn }: { text: string; warn: boolean }) {
  return (
    <span
      className={
        warn
          ? 'whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-xs font-semibold text-amber-700'
          : 'whitespace-nowrap rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-px text-xs font-semibold text-zinc-600'
      }
    >
      {text}
    </span>
  );
}

/**
 * Model names in the catalog often embed the manufacturer ("SUPER MICRO
 * COMPUTER SYS-521GE..."), which reads twice next to the manufacturer chip —
 * strip the prefix so the brand appears exactly once in the chain.
 */
export function stripMfrPrefix(model: string | null, manufacturer: string | null): string | null {
  if (!model || !manufacturer) return model;
  const m = model.trim();
  const p = manufacturer.trim();
  if (!m.toLowerCase().startsWith(p.toLowerCase())) return m;
  const rest = m.slice(p.length).replace(/^[\s\-–·:]+/, '').trim();
  return rest || m;
}

/**
 * Single source of truth for "can this asset be listed for sale" (SIL-337) —
 * shared by the Lifecycle timeline's dashed "Next step" node and
 * `ListForSaleCard`'s render gate (route file). An asset is listable here if
 * it's spot-listable OR still `active` (forward-sale eligible, RUF-308/669).
 */
export function assetIsListable(asset: {
  lifecycleAllowsListing: boolean;
  lifecycleStateId: string | null;
}): boolean {
  return asset.lifecycleAllowsListing || asset.lifecycleStateId === 'active';
}

// ---------------------------------------------------------------------------
// Lifecycle event derivation — shared by LifecycleTab's timeline.
// ---------------------------------------------------------------------------

export interface LifeEvent {
  label: string;
  date: string; // ISO
  record: string; // short record id backing the event
  /** Stable unique key. Derived events omit it (their label is unique); lifecycle
   * transitions set it to the event id so repeated to_states don't collide. */
  id?: string;
}

/** Past-tense label for a lifecycle-stage transition, keyed on the target state
 * so it reads as an event ("Marked idle") alongside Registered / FMV priced. */
export function lifecycleEventLabel(toState: string): string {
  switch (toState) {
    case 'idle':
      return 'Marked idle';
    case 'decommissioned':
      return 'Decommissioned';
    case 'active':
      return 'Returned to active';
    default:
      return `Set to ${lifecycleLabel(toState)}`;
  }
}

/** Past-tense label + record string for a location_change or team_change event
 * (migration 678, RUF-448/RUF-380) — owner-facing timeline only, never rendered
 * on the buyer passport. */
export function locationOrTeamEventLabel(
  ev: Extract<ClientLifecycleEvent, { eventType: 'location_change' | 'team_change' }>,
): { label: string; record: string } {
  if (ev.eventType === 'team_change') {
    return {
      label: ev.toLabel ? `Reassigned to ${ev.toLabel} team` : 'Unassigned from team',
      record: ev.fromLabel && ev.toLabel ? `${ev.fromLabel} → ${ev.toLabel}` : (ev.toLabel ?? ev.fromLabel ?? '—'),
    };
  }
  // location_change
  if (ev.fromLabel && ev.toLabel) {
    return { label: `Moved from ${ev.fromLabel} to ${ev.toLabel}`, record: `${ev.fromLabel} → ${ev.toLabel}` };
  }
  if (ev.toLabel) {
    return { label: `Moved to ${ev.toLabel}`, record: ev.toLabel };
  }
  return { label: 'Location cleared', record: ev.fromLabel ?? '—' };
}
